import { Collapse } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LinkWrapper from '../../LinkWrapper';
import BetaChip from '../../../components/BetaChip';
import { MenuIcon, SubmenuIcon } from './menuIcons';
import { menuLabel } from './menuLabel';
import { hrefFor, useMenuNavigation } from './useMenuNavigation';
import type { Menu } from './types';

interface SidebarCollapsableItemProps {
    menu: Menu;
    isOpen: boolean;
    onToggle: () => void;
}

/**
 * Menú con submenús. Mismo tratamiento que sporttia-competitions: la cabecera
 * se marca con borde y texto verde marca cuando la página actual está dentro del
 * grupo, y el submenú activo lleva relleno verde oliva con texto verde brillante.
 */
export default function SidebarCollapsableItem({
    menu,
    isOpen,
    onToggle,
}: SidebarCollapsableItemProps): React.ReactElement {
    const { t } = useTranslation();
    const { go, isActive, isMenuActive } = useMenuNavigation();
    const isActiveGroup = isMenuActive(menu);

    const headerClassName = [
        'flex h-8 w-full cursor-pointer items-center gap-2 rounded-md p-2 text-sm transition-colors',
        isActiveGroup
            ? 'text-sidebar-primary border-sidebar-primary border font-medium'
            : isOpen
              ? 'text-white font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    ].join(' ');

    return (
        <li id={`tour-menu-${menu.id}`}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                className={headerClassName}
            >
                <MenuIcon id={menu.id} />
                <span className="flex-1 truncate text-left">
                    {menuLabel(t, menu.caption)}
                </span>
                <ChevronDown
                    className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            <Collapse in={isOpen} timeout={300} unmountOnExit>
                <ul className="border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5">
                    {(menu.submenus ?? []).map((submenu) => {
                        const active = isActive(submenu);

                        return (
                            <li
                                key={`${submenu.path ?? submenu.url}-${submenu.caption}`}
                                id={
                                    submenu.path
                                        ? `tour-submenu-${submenu.path}`
                                        : undefined
                                }
                            >
                                <LinkWrapper
                                    href={hrefFor(submenu)}
                                    callback={() => go(submenu)}
                                >
                                    <span
                                        className={`flex h-7 w-full min-w-0 items-center gap-2 rounded-md px-2 text-sm transition-colors ${
                                            active
                                                ? 'bg-sidebar-active text-sidebar-active-foreground font-medium'
                                                : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                                        }`}
                                    >
                                        <SubmenuIcon path={submenu.path} />
                                        <span className="truncate">
                                            {menuLabel(t, submenu.caption)}
                                        </span>
                                        {submenu.beta && <BetaChip />}
                                    </span>
                                </LinkWrapper>
                            </li>
                        );
                    })}
                </ul>
            </Collapse>
        </li>
    );
}
